import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui/spinner.tsx';
import { Button } from '@/components/ui/button.tsx';
import { useAuth } from '@/contexts/AuthContext.tsx';
import config from '@/config';

interface BanGuardProps {
  children: ReactNode;
}

export function BanGuard({ children }: BanGuardProps) {
  const [ isPaymentLoading, setIsPaymentLoading ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ banImage, setBanImage ] = useState<string | null>(null);
  const [ error, setError ] = useState<string | null>(null);

  const { user, getIdToken } = useAuth();

  const handleBanLiftPayment = useCallback(async () => {
    setIsPaymentLoading(true);
    try {
      const response = await fetch(`${config.baseUrl}/${config.endpoints.createCheckoutSession}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getIdToken()}`
        },
        body: JSON.stringify({
          productIds: [config.paymentProducts.banLift],
          purpose: 'ban_lift',
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payload && data.payload.url) {
          window.location.href = data.payload.url;
        }
      } else {
        setError('Unable to process payment');
      }
    } catch (error) {
      console.error('Failed to initiate ban lift payment:', error);
    } finally {
      setIsPaymentLoading(false);
    }
  }, [ getIdToken ]);

  useEffect(() => {
    if (user?.maybeBannedDetails) {
      // get ban evidence images
      (async () => {
        try {
          setIsLoading(true);

          const response = await fetch(`${config.baseUrl}/${config.endpoints.banEvidenceImages}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getIdToken()}`
            },
            body: JSON.stringify({
              productId: config.paymentProducts.banLift,
              evidence: user.maybeBannedDetails?.evidence,
              reason: user.maybeBannedDetails?.reason
            })
          });

          if (response.ok) {
            const data = await response.json();
            setBanImage(data.payload?.image);
          } else {
            console.error('Failed to fetch ban evidence images:', response.statusText);
          }
        } catch (e) {
          console.error('Failed to fetch base URL:', e);
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      setIsLoading(false);
    }
  }, [ getIdToken, user?.maybeBannedDetails ]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <Spinner
          size="large"
          variant="primary"
          text="Checking user status..."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400">Please try again later</p>
        </div>
      </div>
    );
  }

  // Show banned UI
  if (user?.maybeBannedDetails) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">You are banned</h1>
          <p className="text-gray-400 mb-6">{user.maybeBannedDetails.reason}</p>
          {banImage &&
            <div className="mt-4 mb-6">
              <img
                src={`data:image/png;base64,${banImage}`}
                alt="Ban related image"
                className="max-w-md mx-auto rounded-lg shadow-lg"
              />
            </div>
          }
          <Button
            onClick={handleBanLiftPayment}
            disabled={isPaymentLoading}
            className="bg-[#11B981] hover:bg-[#0EA874] text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
          >
            {isPaymentLoading ? (
              <>
                <Spinner size="small"/>
                <span className="ml-2">Processing...</span>
              </>
            ) : (
              'Pay to Lift Ban'
            )}
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}